// store/integrationInstructions.ts
//
// Data-driven copy for the "grant vGrubs access" instructions shown during onboarding and in
// the Settings > POS Integration Setup guide. Editing text here updates every surface.
import { PosType } from '../types/api/location.types';
// POS guide screenshots (extracted from the official Clover/Toast onboarding PDFs).
import cloverGuideEmployees from '../assets/images/guides/clover/clover-employees.png';
import cloverGuideAddEmployee from '../assets/images/guides/clover/clover-add-employee.png';
import cloverGuideFillDetails from '../assets/images/guides/clover/clover-fill-details.png';
import cloverGuideSave from '../assets/images/guides/clover/clover-save.png';
import toastGuideLogin from '../assets/images/guides/toast/toast-login.png';
import toastGuideDashboard from '../assets/images/guides/toast/toast-dashboard-employees.png';
import toastGuideEmployeeManagement from '../assets/images/guides/toast/toast-employee-management.png';
import toastGuideAddEmployee from '../assets/images/guides/toast/toast-add-employee.png';
import toastGuideActiveStatus from '../assets/images/guides/toast/toast-active-status.png';
import toastGuideJobsPermissions from '../assets/images/guides/toast/toast-jobs-permissions.png';
import toastGuidePermissions from '../assets/images/guides/toast/toast-permissions.png';

export type IntegrationInstruction = {
  /** Ordered, plain-language steps. `{portal}` renders as a link to portalUrl. */
  steps: string[];
  /** A detailed step-by-step "full guide" (see posGuides) is available in a modal. */
  hasFullGuide?: boolean;
  /**
   * Where the partner does the steps, opened in a new tab. The server sends these for the
   * marketplaces it knows; what is here is the fallback for the rest.
   */
  portalUrl?: string;
  portalLabel?: string;
};

/**
 * The vGrubs account partners add in their POS. The server decides every access email and
 * sends it with the access data; this one is only for the static Clover/Toast guide text.
 */
export const POS_ACCESS_EMAIL = 'pos@vgrubs.com';

// ─── Marketplaces ───────────────────────────────────────────────────────────
const defaultProviderInstruction = (email: string): IntegrationInstruction => ({
  steps: [
    'Sign in to the marketplace manager or merchant portal.',
    `Add ${email} as a user with the highest (admin) permissions, enough to edit the menu, working hours and orders.`,
    'Save, then tap "I have added the email".',
  ],
});

/** Keyed by a normalized provider name (lowercase, no spaces). */
const providerInstructions: Record<
  string,
  (email: string) => IntegrationInstruction
> = {
  grubhub: (email) => ({
    portalUrl: 'https://restaurant.grubhub.com/',
    portalLabel: 'Grubhub for Restaurants',
    steps: [
      'Sign in to {portal} as an admin.',
      `Invite ${email} with the highest permission level so we can edit the menu, working hours and orders.`,
      'When creating the user, set the password shown below (copy it).',
      'Add anything we should know in the notes, then tap "I have added the email".',
    ],
  }),
  ubereats: (email) => ({
    portalUrl: 'https://merchants.ubereats.com/',
    portalLabel: 'Uber Eats Manager',
    steps: [
      'Open {portal} and go to Users.',
      `Invite ${email} as an Admin (highest role) so we can edit the menu, working hours and orders.`,
      'Send the invite, then tap "I have added the email".',
    ],
  }),
  doordash: (email) => ({
    portalUrl: 'https://www.doordash.com/merchant/login',
    portalLabel: 'DoorDash Merchant Portal',
    steps: [
      'Open the {portal} and go to Settings, then Manage Users.',
      `Add ${email} as a Manager or Admin (highest access) so we can edit the menu, working hours and orders.`,
      'Save, then tap "I have added the email".',
    ],
  }),
};

const normalize = (name: string) => name.toLowerCase().replace(/\s+/g, '');

/** Steps for one marketplace, with the access email the server assigned to it. */
export const getProviderInstruction = (
  name: string,
  accessEmail: string
): IntegrationInstruction =>
  providerInstructions[normalize(name)]?.(accessEmail) ??
  defaultProviderInstruction(accessEmail);

// ─── POS ────────────────────────────────────────────────────────────────────
const defaultPosInstruction = (email: string): IntegrationInstruction => ({
  steps: [
    'Log in to your POS dashboard as the account owner.',
    `Add ${email} as a user with the highest permissions.`,
    'Come back and tap “I’ve granted access” so our team can connect it.',
  ],
});

const posInstructions: Partial<
  Record<PosType, (email: string) => IntegrationInstruction>
> = {
  [PosType.clover]: (email) => ({
    hasFullGuide: true,
    steps: [
      'Log in to your Clover Dashboard as the account owner.',
      `Add ${email} as an Admin employee (see the full guide for details).`,
      'Tap “I’ve granted access” once done.',
    ],
  }),
  [PosType.toast]: (email) => ({
    hasFullGuide: true,
    steps: [
      'Sign in to Toast Web as an admin.',
      `Invite ${email} with the highest access (see the full guide for details).`,
      'Tap “I’ve granted access” once done.',
    ],
  }),
  [PosType.lightspeedO]: (email) => ({
    steps: [
      'Log in to your Lightspeed back office as the owner.',
      `Create a user for ${email} with full/admin permissions.`,
      'Save, then tap “I’ve granted access”.',
    ],
  }),
};

/** Steps for one POS, with the access email the server assigned to it. */
export const getPosInstruction = (
  type: PosType,
  accessEmail: string
): IntegrationInstruction =>
  posInstructions[type]?.(accessEmail) ?? defaultPosInstruction(accessEmail);

// ─── POS "full guide" content (transcribed from the official Clover/Toast PDFs) ─
/** A plain sentence, or a field the partner has to type in (label, the exact value, a hint). */
export type PosGuideDetail =
  | string
  | { label: string; value: string; hint?: string };
export type PosGuideStep = {
  title: string;
  details: PosGuideDetail[];
  note?: string;
  /** Screenshot(s) illustrating this step, shown beneath the details in the guide modal. */
  images?: string[];
};
export type PosGuideFaq = { q: string; a: string };
export type PosGuide = {
  title: string;
  intro?: string;
  /** `copy` marks values the partner types into the POS; those get click-to-copy. */
  quickReference: { label: string; value: string; copy?: boolean }[];
  steps: PosGuideStep[];
  faq?: PosGuideFaq[];
};

export const posGuides: Partial<Record<PosType, PosGuide>> = {
  [PosType.clover]: {
    title: 'Clover POS Access',
    intro:
      'Step-by-step guide for restaurant owners · No technical knowledge required · ~5 min',
    quickReference: [
      { label: 'Platform', value: 'clover.com', copy: true },
      { label: 'Email to add', value: POS_ACCESS_EMAIL, copy: true },
      { label: 'Role', value: 'Admin (or highest available)' },
      { label: 'Estimated time', value: '3–5 minutes' },
    ],
    steps: [
      {
        title: 'Log in to your Clover account',
        details: [
          'Open your browser and go to clover.com.',
          'Click Log In and enter your owner credentials.',
          'You will land on your Clover Dashboard — your restaurant name appears in the top-right corner.',
        ],
      },
      {
        title: 'Go to Employees',
        details: [
          'In the left-hand sidebar, click Employees.',
          'In the sub-menu that appears, click Employees list.',
        ],
        images: [cloverGuideEmployees],
      },
      {
        title: 'Click "+ Add employee"',
        details: [
          'On the Employees page, click the green + Add employee button in the top-right corner.',
        ],
        images: [cloverGuideAddEmployee],
      },
      {
        title: 'Fill in the employee details exactly as shown',
        details: [
          { label: 'Full Name', value: 'vGrubs Tech' },
          { label: 'Nickname', value: 'vGrubs' },
          { label: 'Phone number', value: '(212) 518-8637' },
          { label: 'Email address', value: POS_ACCESS_EMAIL },
          'Role: Admin (or highest available role)',
          'Device passcode: leave blank, one will be assigned automatically.',
        ],
        note: `Make sure the Role is set to Admin. If you do not see Admin, select the highest available role. The email ${POS_ACCESS_EMAIL} is required — type it carefully.`,
        images: [cloverGuideFillDetails],
      },
      {
        title: 'Click "Save"',
        details: [
          'Click the green Save button in the bottom-right corner.',
          'vGrubs Tech will now appear in your Employees list.',
        ],
        images: [cloverGuideSave],
      },
      {
        title: 'Notify your vGrubs account manager',
        details: [
          `Send a quick message: "I have added ${POS_ACCESS_EMAIL} as Admin on Clover."`,
          'Our team will take over from here — no further action needed on your side.',
        ],
        note: 'Setup complete! Once we confirm access, your integration will be up and running.',
      },
    ],
    faq: [
      {
        q: 'I cannot find the Employees section in the sidebar',
        a: 'Click Employees in the left sidebar. If you do not see it, make sure you are logged in as the account Owner — only Owners can add new employees with Admin access.',
      },
      {
        q: 'The "+ Add employee" button is missing or grayed out',
        a: 'Only account Owners can add employees. Log out and log back in with the owner credentials. If you do not have them, ask whoever set up your Clover account.',
      },
      {
        q: 'I do not see Admin in the Role dropdown',
        a: 'Select the highest available role in the dropdown. Let your vGrubs account manager know which role you selected so we can confirm the access level is sufficient.',
      },
      {
        q: 'Do I need to set a Device passcode?',
        a: 'No — leave it blank. Clover will automatically assign a passcode if needed.',
      },
      {
        q: 'vGrubs did not receive an invitation email',
        a: `Double-check that you entered ${POS_ACCESS_EMAIL} with no typos. If it was not received, go back to the Employees list, click on vGrubs Tech, and verify the email address is correct.`,
      },
    ],
  },

  [PosType.toast]: {
    title: 'Toast POS Access',
    intro:
      'Step-by-step guide for restaurant owners · No technical knowledge required · ~10 min',
    quickReference: [
      { label: 'Email to add', value: POS_ACCESS_EMAIL, copy: true },
      { label: 'POS Access Code', value: '4311', copy: true },
      { label: 'Job / Role', value: 'General Manager (or highest available)' },
      { label: 'Platform', value: 'toasttab.com', copy: true },
      { label: 'Estimated time', value: '5–10 minutes' },
    ],
    steps: [
      {
        title: 'Log in to your Toast account',
        details: [
          'Open your browser and go to toasttab.com.',
          'Click Log In in the top-right corner and enter your owner credentials.',
          'You will land on your Toast Dashboard — your restaurant name appears in the top navigation bar.',
        ],
        images: [toastGuideLogin],
      },
      {
        title: 'Navigate to Employees',
        details: [
          'In the left-hand sidebar, click Employees (person icon).',
          'In the sub-menu, click Employee management.',
          'Then click Employees — "Add, edit, and archive employees. Manage employee permissions and passcodes."',
        ],
        note: 'You must be logged in as an Owner or Admin to add new users. If you cannot see the Employees section, ask whoever set up your Toast account.',
        images: [toastGuideDashboard, toastGuideEmployeeManagement],
      },
      {
        title: 'Click the blue "+ Add New Employee" button',
        details: [
          'On the Employees list page, click + Add New Employee (top-left, above the list).',
          'A panel slides in from the right side of the screen.',
          'At the top you will see "Invite to create account" — make sure Yes is selected (highlighted in dark blue).',
        ],
        images: [toastGuideAddEmployee],
      },
      {
        title: 'Fill in the employee details exactly as shown',
        details: [
          { label: 'First name', value: 'vGrubs' },
          { label: 'Last name', value: 'Tech' },
          { label: 'Mobile phone', value: '(212) 518-8637' },
          {
            label: 'Email address',
            value: POS_ACCESS_EMAIL,
            hint: 'login email',
          },
          {
            label: 'POS access code',
            value: '4311',
            hint: '4-digit PIN for the physical terminal',
          },
          'Job / Role: General Manager (or anything similar), select from the dropdown.',
        ],
        note: `Make sure "Invite to create account" is set to Yes before clicking Add. This sends a login invitation to ${POS_ACCESS_EMAIL}.`,
      },
      {
        title: 'Save — click the blue "Add" button',
        details: [
          'Click Add at the bottom-right of the panel.',
          'vGrubs Tech will now appear in your employee list with status Active.',
          'A checkmark appears in the Account Created column once our team accepts the invitation.',
        ],
        images: [toastGuideActiveStatus],
      },
      {
        title: 'Open the vGrubs Tech profile — click the pencil icon',
        details: [
          'Find vGrubs Tech in your employee list.',
          'Click the pencil (edit) icon on the right side of that row.',
          'The employee profile opens — click the Jobs & Permissions tab at the top.',
        ],
        images: [toastGuideJobsPermissions],
      },
      {
        title: 'Click "Permissions" in the left sub-menu',
        details: [
          'On the left you will see two options: Jobs & pay and Permissions.',
          'Click Permissions.',
          'You will see a numbered list of permission sections with Inherit and Override buttons next to each one.',
        ],
      },
      {
        title: 'Enable all permissions — Override + check every box',
        details: [
          'For every row: click Override, then check the checkbox next to it.',
          'Scroll through all sections and enable everything.',
        ],
        note: 'Each row has two buttons — Inherit and Override. Click Override first, then check the box. A blue checkmark means it is enabled. Repeat for every row.',
        images: [toastGuidePermissions],
      },
      {
        title: 'Click Save',
        details: [
          'Click Save in the top-right corner of the profile page.',
          'You will be returned to the employee list.',
        ],
        note: 'Setup complete! One last step — notify your vGrubs account manager.',
      },
      {
        title: 'Notify your vGrubs account manager',
        details: [
          `Send a quick message: "I have added ${POS_ACCESS_EMAIL} as General Manager on Toast with full permissions."`,
          'Our team will take over from here — no further action needed on your side.',
        ],
      },
    ],
    faq: [
      {
        q: 'I cannot find the Employees section in the sidebar',
        a: 'Scroll down in the left sidebar until you see the Employees icon (person silhouette). If it is missing entirely, you may not have Admin or Owner access — ask the restaurant owner to complete these steps.',
      },
      {
        q: 'The "+ Add New Employee" button is missing or grayed out',
        a: 'Only account Owners and Admins can add employees. Log out and log back in with the owner credentials. If you do not have them, forward this guide to the restaurant owner.',
      },
      {
        q: 'The Override button is disabled — I cannot change permissions',
        a: 'This means you are logged in as a Manager, not an Owner. Switch to the Owner account and try again. Only the account Owner can modify permission overrides.',
      },
      {
        q: 'vGrubs did not receive the invitation email',
        a: `Double-check that you entered ${POS_ACCESS_EMAIL} with no typos. To resend: go to the employee list, click the pencil icon next to vGrubs Tech, and look for Resend Invitation on the Overview tab.`,
      },
      {
        q: 'Which Job title should I select if General Manager is not available?',
        a: 'Select the highest-level role available (e.g. Manager, Shift Lead). Avoid roles like Staff, Cashier, or Runner — these come with restricted permissions that may limit our integration.',
      },
    ],
  },
};

export const getPosGuide = (type: PosType): PosGuide | undefined =>
  posGuides[type];
